<template>
  <div
    class="flow-group"
    @contextmenu="openContextMenu"
    @click="closeContextMenu"
    :data-group-built-in="group.built_in"
    :data-group-id="group.id"
  >
    <context-menu ref="contextMenu">
      <context-menu-option
        :disabled="!allowCopyInContextMenu"
        @click="copyBlock"
        >{{ $t("btn.copy") }}</context-menu-option
      >
      <context-menu-option
        :disabled="!allowPasteInContextMenu"
        @click="pasteBlock"
        >{{ $t("btn.paste") }}</context-menu-option
      >
      <context-menu-option @click="closeContextMenu">{{
        $t("btn.close")
      }}</context-menu-option>
    </context-menu>
    <popup
      :title="$t('view.flow.dialog.deleteGroup.title')"
      ref="groupDelDialog"
    >
      <p class="flow-group__popup-text">
        {{ $t("view.flow.dialog.deleteGroup.text") }}
        <b>{{ group.name }}</b>
        <in
          :placeholder="group.name"
          :label="$t('view.flow.dialog.deleteGroup.inputLabel')"
          class="flow-group__popup-input"
          type="text"
          v-model="delPasscode"
        ></in>
      </p>
      <template v-slot:buttons>
        <btn icon="times" @click="$refs.groupDelDialog.close()">{{
          $t("btn.cancel")
        }}</btn>
        <btn icon="trash" @click="deleteGroup" color="red">{{
          $t("btn.delete")
        }}</btn>
      </template>
    </popup>

    <!-- Group rename controlls -->
    <div class="flow-group__header" @click="toggle">
      <div
        class="flow-group__toggle"
        :class="{ 'flow-group__toggle--active': show }"
      >
        <fai icon="caret-right"></fai>
      </div>
      <h4 v-if="!editMode">{{ group.name }}</h4>

      <in
        class="flow-group__input"
        v-show="editMode"
        type="text"
        @click="stopEv"
        v-model="editedName"
      ></in>

      <div class="flow-group__icon" v-if="!editMode" @click="openRename">
        <fai icon="pencil-alt"></fai>
      </div>
      <div
        class="flow-group__icon"
        v-if="!group.built_in && !editMode"
        @click="openDeleteDialog"
      >
        <fai icon="trash"></fai>
      </div>
      <div
        class="flow-group__icon flow-group__drag-handler"
        v-if="!group.built_in && !editMode"
      >
        <fai icon="arrows-alt-v"></fai>
      </div>
      <div
        class="flow-group__icon flow-group__icon--always-visible"
        v-if="editMode"
        @click="saveRename"
      >
        <fai icon="save"></fai>
      </div>
      <div
        class="flow-group__icon flow-group__icon--always-visible"
        v-if="editMode"
        @click="cancelRename"
      >
        <fai icon="times"></fai>
      </div>
    </div>
    <!-- Group blocks -->
    <div
      class="flow-group__content"
      :class="{ 'flow-group__content--hidden': !show }"
    >
      <draggable
        v-if="!group.built_in"
        style="width: 100%"
        v-model="group.blocks"
        @update="blockMoved"
      >
        <short-card
          class="flow-group__block"
          v-for="block in group.blocks"
          :key="block.id"
          :data-uuid="block.uuid"
          @selected="selectBlock(block)"
          :isSelected="block.active"
        >
          <div>{{ block.name }}</div>
          <div>{{ block.description }}</div>
        </short-card>
      </draggable>
      <div v-else>
        <short-card
          class="flow-group__block"
          v-for="block in group.blocks"
          :key="block.id"
          :data-uuid="block.uuid"
          @selected="selectBlock(block)"
          :isSelected="block.active"
        >
          <div>{{ block.name }}</div>
          <div>{{ block.description }}</div>
        </short-card>
      </div>
      <div
        @click="createBlock()"
        class="flow-group__add-block"
        v-if="!group.built_in"
      >
        {{ $t("view.flow.newBlock") }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/axios";

export default {
  props: ["group"],
  data() {
    return {
      show: false,
      editMode: false,
      editedName: "",
      delPasscode: "",
      allowCopyInContextMenu: false,
      allowPasteInContextMenu: false,
      blockUuidToCopy: null,
      groupIdToPaste: null,
    };
  },

  mounted() {
    if (this.group.built_in) this.show = true;
  },

  methods: {
    toggle() {
      this.show = !this.show;
    },

    clearChoice() {
      for (let block of this.group.blocks) {
        this.$set(block, "active", false);
      }
    },

    selectBlock(block) {
      if (block.active) return;
      block.built_in = this.group.built_in;
      this.$emit("clearSelected");
      this.group.blocks.map((b, i) => {
        if (b.id === block.id) this.$set(this.group.blocks[i], "active", true);
      });
      this.$emit("selected", block);
    },

    stopEv(e) {
      e.stopPropagation();
    },

    openRename(e) {
      this.editedName = this.group.name;
      this.editMode = true;
      e.stopPropagation();
    },

    async saveRename(e) {
      try {
        e.stopPropagation();
        if (this.editedName === this.group.name) return this.cancelRename(e);
        await axios.put("/api/flow/group/name", {
          id: this.group.id,
          name: this.editedName,
        });
        this.$store.commit("UPDATE_GROUP_NAME", {
          name: this.editedName,
          id: this.group.id,
        });
        this.editedName = "";
        this.editMode = false;
        this.notify("success", "view.flow.success.groupRename");
      } catch (e) {
        console.error(e);
        this.notify("error", "view.flow.error.groupRename");
      }
    },

    cancelRename(e) {
      this.editMode = false;
      this.editedName = "";
      e.stopPropagation();
    },

    openDeleteDialog(e) {
      if (this.group.blocks.length) this.$refs.groupDelDialog.open();
      else this.deleteGroup();
      e.stopPropagation();
    },

    async deleteGroup() {
      try {
        if (this.group.blocks.length && this.group.name !== this.delPasscode) {
          this.$refs.groupDelDialog.close();
          this.notify("error", "view.flow.error.groupDeletePassword");
          return;
        }
        await axios.delete("/api/flow/group", {
          data: {
            id: this.group.id,
          },
        });
        await this.$refs.groupDelDialog.close();
        this.$store.commit("DELETE_GROUP", this.group.id);
        this.delPasscode = "";
        this.notify("success", "view.flow.success.groupDelete");
      } catch (e) {
        console.error(e);
        this.$refs.groupDelDialog.close();
        this.notify("error", "view.flow.error.groupDelete");
      }
    },

    async createBlock(name, groupId, silent) {
      try {
        const blockReq = await axios.post("/api/flow/block", {
          name: name || this.$t("view.flow.defaultBlockName"),
          group_id: groupId || this.group.id,
        });
        this.$store.commit("ADD_BLOCK_TO_GROUP", {
          block: blockReq.data.created,
          group: this.group.id,
        });
        if (!silent) this.notify("success", "view.flow.success.blockCreate");
        return blockReq.data.created;
      } catch (e) {
        console.error(e);
        if (!silent) this.notify("error", "view.flow.error.blockCreate");
      }
    },

    openContextMenu(e) {
      e.preventDefault();
      const path = e.composedPath ? e.composedPath() : e.path;
      const blockClicked = path.filter(
        (el) => el.classList && el.classList.contains("flow-group__block")
      )[0];
      const groupClicked = path.filter(
        (el) => el.classList && el.classList.contains("flow-group")
      )[0];
      if (groupClicked) {
        this.$store.commit(
          "FLOW_BLOCK_PASTE_TARGET",
          parseInt(groupClicked.dataset.groupId)
        );
        if (groupClicked.dataset.groupBuiltIn === "true") return;
        this.allowPasteInContextMenu = true;
      }
      if (blockClicked) {
        this.allowCopyInContextMenu = true;
        this.blockUuidToCopy = blockClicked.dataset.uuid;
      } else {
        this.allowCopyInContextMenu = false;
      }
      this.$refs.contextMenu.open(e);
      for (const g of this.$parent.$parent.$refs.flowGroups) {
        if (g === this) continue;
        g.closeContextMenu();
      }
    },

    closeContextMenu() {
      this.$refs.contextMenu.close();
    },

    removeBlockIdentifiers(obj) {
      for (const prop in obj) {
        if (["id", "uuid", "uid"].indexOf(prop) !== -1) delete obj[prop];
        else if (typeof obj[prop] === "object")
          this.removeBlockIdentifiers(obj[prop]);
      }
    },

    copyBlock() {
      const deepCpy = JSON.parse(
        JSON.stringify(this.$store.getters.flowBlock(this.blockUuidToCopy))
      );
      this.removeBlockIdentifiers(deepCpy);
      this.$store.commit("COPY_FLOW_BLOCK", JSON.stringify(deepCpy));
    },

    async pasteBlock() {
      try {
        const blockToPaste = JSON.parse(this.$store.state.flowCopiedBlock);
        const targetGroupId = this.$store.state.flowBlockPasteTarget;
        const name = ("(C) " + blockToPaste.name).substring(0, 32);
        const block = await this.createBlock(name, targetGroupId, true);
        const blockData =
          this.$parent.$parent.$refs.blockEditor.prepareBlockData(
            blockToPaste.data
          );
        const preparedSteps =
          this.$parent.$parent.$refs.blockEditor.$refs.stepsEditor.prepareData(
            blockToPaste.data.steps
          );

        await axios.put("/api/flow/block/data", {
          blockId: block.id,
          blockData,
        });
        block.data = {
          variables: blockData.variables,
          steps: preparedSteps,
        };
        this.notify("success", "view.flow.success.blockCopy");
        // If buggy, we should try running hooks, but I don't think it's needed as JSON is copied so S3 links are tha same
      } catch (e) {
        console.error(e);
        this.notify("error", "view.flow.error.blockCopy");
      }
    },

    async blockMoved(e) {
      const eCp = e;
      try {
        await axios.put("/api/flow/blocks/order", {
          blockUuid: e.item.dataset.uuid,
          groupId: this.group.id,
          newIndex: e.newIndex,
        });
      } catch (e) {
        const [el] = this.group.blocks.splice(eCp.newIndex, 1);
        this.group.blocks.splice(eCp.oldIndex, 0, el);
        console.log(this.group.blocks);
        console.error(e);
        this.notify("error", "view.flow.error.blockMove");
      }
    },
  },
};
</script>

<style lang="scss">
.flow-group {
  width: 90%;
  margin: 0 auto;

  &__popup-text {
    width: 400px;
    margin: 0 auto 20px auto;
  }

  &__popup-input {
    width: 70%;
    margin: 15px auto 0 auto;
  }

  &__header {
    display: flex;
    align-items: center;
    text-align: left;
    cursor: pointer;
    padding: 4px 0;

    h4 {
      margin: 0;
      margin-left: 10px;
      margin-top: 5px;
    }

    &:hover {
      .flow-group__icon {
        display: block;
      }
    }
  }

  &__toggle {
    color: #929292;
    transition: transform 0.2s ease;

    &--active {
      transform: rotate(90deg);
    }
  }

  &__icon {
    margin-left: 10px;
    margin-top: 4px;
    color: #929292;
    display: none;

    &--always-visible {
      display: block;
    }
  }

  &__input {
    margin-left: 7px;
    margin-right: 4px;
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    justify-items: flex-start;
    padding-top: 5px;
    max-height: 2000px;
    height: auto;
    will-change: max-height;
    transition: max-height 0.3s ease-out;
    overflow-y: auto;

    &--hidden {
      overflow: hidden;
      max-height: 0;
    }
  }

  &__block {
    margin-bottom: 10px;
    width: 100%;
  }

  &__add-block {
    @include clickableText();
    margin-left: 5%;
    margin-top: 6px;
    margin-bottom: 6px;
  }
}
</style>
